import { useEffect, useState } from 'react';

import type { HotelReadCache } from '../hotel_read_cache.js';
import type { HotelMoneyView, HotelOfferView } from '../models.js';

type DetailWithRooms = { readonly rooms: readonly unknown[] };

/** Detalhe RESOLVIDO sem nenhum quarto: o mesmo sinal com que a Página do
 *  Hotel diz "Este hotel não tem quarto disponível para as datas da busca". */
function isSoldOut<S, D extends DetailWithRooms>(
  cache: HotelReadCache<S, D>,
  offerId: string,
): boolean {
  const detail = cache.details.get(offerId);
  return detail !== undefined && !(detail instanceof Promise) && detail.rooms.length === 0;
}

function soldOutIn<S, D extends DetailWithRooms>(
  cache: HotelReadCache<S, D> | undefined,
): ReadonlySet<string> {
  if (!cache) return new Set();
  return new Set([...cache.details.keys()].filter((offerId) => isSoldOut(cache, offerId)));
}

/**
 * #4469: os hotéis cujo detalhe já voltou sem quarto. A lista vive no estado do
 * cliente e não relê o servidor ao voltar da página — sem isto o card seguia
 * com a diária do cache da busca para um hotel que a página acabou de dizer
 * que não tem quarto.
 */
export function useSoldOutOfferIds<S, D extends DetailWithRooms>(
  cache: HotelReadCache<S, D> | undefined,
): ReadonlySet<string> {
  const [soldOut, setSoldOut] = useState(() => soldOutIn(cache));
  // A new cache starts from what it already knows (during render, on the change).
  const [readCache, setReadCache] = useState(cache);
  if (readCache !== cache) {
    setReadCache(cache);
    setSoldOut(soldOutIn(cache));
  }
  useEffect(() => {
    if (!cache) return undefined;
    const onDetail = (offerId: string) => {
      const now = isSoldOut(cache, offerId);
      setSoldOut((previous) => {
        if (previous.has(offerId) === now) return previous;
        const next = new Set(previous);
        if (now) next.add(offerId);
        else next.delete(offerId);
        return next;
      });
    };
    cache.detailListeners.add(onDetail);
    return () => {
      cache.detailListeners.delete(onDetail);
    };
  }, [cache]);
  return soldOut;
}

type RatedOffer = {
  price?: HotelMoneyView;
  refundable?: boolean;
  refundableRateFrom?: HotelMoneyView;
};

/** Sem quarto, a oferta não sustenta preço nem condição de tarifa: sai da faixa
 *  do filtro de diária e vai para o fim da ordem por preço. */
export function withoutSoldOutRate<T extends RatedOffer>(offer: T): T {
  const rest = { ...offer };
  delete rest.price;
  delete rest.refundable;
  delete rest.refundableRateFrom;
  return rest;
}

/** O card diz o porquê (`label`) em vez de "Preço a confirmar" — o mesmo rótulo
 *  no pin. */
export function soldOutView(view: HotelOfferView, label: string): HotelOfferView {
  const rest = { ...view, priceLabel: label, bookable: false };
  delete rest.priceDetail;
  delete rest.refundableLabel;
  return rest;
}
